/**
 * Web Agent Tool Bridge
 *
 * Publishes in-page, MCP-shaped tools to browser-resident AI agents through two
 * channels at once:
 *
 * 1. The WebMCP standard (`document.modelContext.registerTool`) when the browser
 *    implements it, with `navigator.modelContext` retained as a legacy fallback. Tools
 *    are already MCP-shaped, so this costs nothing extra and picks up
 *    standards-consuming agents (e.g. Gemini in Chrome) as they ship.
 * 2. A window-level registry (`window.__TREBBLE_AGENT_TOOLS__`) plus a console
 *    discovery note, which gives every JS-eval-capable agent (Claude in Chrome,
 *    Playwright MCP, Chrome DevTools MCP) typed tool semantics today.
 *
 * The bridge owns no editing semantics. Adapters (Motion Studio's StudioToolHost,
 * the editor's audioEditorAutomation surface) register tools whose execute
 * callbacks delegate to the existing production code paths, preserving the
 * "one semantics owner" rule.
 */

export interface WebAgentToolAnnotations {
    readOnlyHint?: boolean;
    destructiveHint?: boolean;
    idempotentHint?: boolean;
    openWorldHint?: boolean;
}

export interface WebAgentToolDefinition {
    name: string;
    title?: string;
    description: string;
    inputSchema: Record<string, unknown>;
    annotations?: WebAgentToolAnnotations;
    execute: (input: Record<string, unknown>) => Promise<unknown>;
}

export interface WebAgentToolSummary {
    name: string;
    title?: string;
    description: string;
    annotations?: WebAgentToolAnnotations;
}

export interface WebAgentToolDescription extends WebAgentToolSummary {
    inputSchema: Record<string, unknown>;
}

interface ModelContextToolResult {
    content: Array<{ type: 'text'; text: string }>;
}

interface ModelContextToolPayload {
    name: string;
    description: string;
    inputSchema: Record<string, unknown>;
    annotations?: WebAgentToolAnnotations;
    execute: (input: Record<string, unknown>) => Promise<ModelContextToolResult>;
}

interface ModelContextApi {
    registerTool?: (tool: ModelContextToolPayload, options?: { signal?: AbortSignal }) => unknown;
    unregisterTool?: (name: string) => void;
    provideContext?: (context: { tools: ModelContextToolPayload[] }) => void;
}

export interface WebAgentWindowRegistry {
    version: number;
    help: () => string;
    list: () => WebAgentToolSummary[];
    describe: (name: string) => WebAgentToolDescription | null;
    call: (name: string, input?: Record<string, unknown>) => Promise<unknown>;
}

const WINDOW_REGISTRY_KEY = '__TREBBLE_AGENT_TOOLS__';
const WINDOW_REGISTRY_VERSION = 1;
const HELP_TEXT = 'Trebble in-page agent tools. list() returns available tools; '
    + 'describe(name) returns the JSON input schema; call(name, input) executes the tool '
    + 'and resolves with a structured result. Tools mutate the open Trebble editing session '
    + 'exactly like the corresponding UI action, so the editor updates live. Tools with '
    + 'readOnlyHint=false may change editor or project state; inspect destructiveHint before '
    + 'invoking destructive actions.';

function getBridgeWindow(): (Window & Record<string, unknown>) | null {
    if (typeof window === 'undefined') return null;
    return window as Window & Record<string, unknown>;
}

function getModelContextApi(): ModelContextApi | null {
    if (typeof document !== 'undefined') {
        const documentModelContext = (document as Document & { modelContext?: ModelContextApi }).modelContext;
        if (documentModelContext) return documentModelContext;
    }
    if (typeof navigator === 'undefined') return null;
    const candidate = (navigator as Navigator & { modelContext?: ModelContextApi }).modelContext;
    return candidate ?? null;
}

function toModelContextResult(result: unknown): ModelContextToolResult {
    let text: string;
    try {
        text = JSON.stringify(result === undefined ? null : result);
    } catch {
        text = String(result);
    }
    return { content: [{ type: 'text', text: text ?? 'null' }] };
}

function toModelContextPayload(tool: WebAgentToolDefinition): ModelContextToolPayload {
    return {
        name: tool.name,
        description: tool.description,
        inputSchema: tool.inputSchema,
        annotations: tool.annotations,
        execute: async (input: Record<string, unknown>) => toModelContextResult(await tool.execute(input ?? {})),
    };
}

export class WebAgentToolBridge {
    private readonly tools = new Map<string, WebAgentToolDefinition>();
    private readonly modelContextAbortControllers = new Map<string, AbortController>();
    private announceQueued = false;
    private lastAnnouncedNames = '';

    registerTool(tool: WebAgentToolDefinition): void {
        if (!tool || typeof tool.name !== 'string' || tool.name.trim() === '') {
            throw new Error('WebAgentToolBridge.registerTool requires a non-empty tool name.');
        }
        if (typeof tool.description !== 'string' || tool.description.trim() === '') {
            throw new Error(`WebAgentToolBridge tool "${tool.name}" requires a description.`);
        }
        if (typeof tool.execute !== 'function') {
            throw new Error(`WebAgentToolBridge tool "${tool.name}" requires an execute function.`);
        }
        if (this.tools.has(tool.name)) {
            throw new Error(`WebAgentToolBridge tool "${tool.name}" is already registered.`);
        }
        this.tools.set(tool.name, tool);
        this.publishToolToModelContext(tool);
        this.refreshWindowRegistry();
        this.queueAnnouncement();
    }

    registerTools(tools: readonly WebAgentToolDefinition[]): void {
        tools.forEach(tool => this.registerTool(tool));
    }

    unregisterTool(name: string): void {
        if (!this.tools.delete(name)) return;
        const controller = this.modelContextAbortControllers.get(name);
        const modelContext = getModelContextApi();
        if (controller) {
            controller.abort();
            this.modelContextAbortControllers.delete(name);
            if (modelContext?.unregisterTool) {
                try {
                    modelContext.unregisterTool(name);
                } catch {
                    // The AbortController signal above is the primary removal path.
                }
            }
        } else if (!modelContext?.registerTool && modelContext?.provideContext) {
            this.provideFullContext(modelContext);
        }
        this.refreshWindowRegistry();
        this.queueAnnouncement();
    }

    unregisterTools(names: readonly string[]): void {
        names.forEach(name => this.unregisterTool(name));
    }

    hasTool(name: string): boolean {
        return this.tools.has(name);
    }

    listTools(): WebAgentToolSummary[] {
        return Array.from(this.tools.values()).map(tool => ({
            name: tool.name,
            title: tool.title,
            description: tool.description,
            annotations: tool.annotations,
        }));
    }

    describeTool(name: string): WebAgentToolDescription | null {
        const tool = this.tools.get(name);
        if (!tool) return null;
        return {
            name: tool.name,
            title: tool.title,
            description: tool.description,
            annotations: tool.annotations,
            inputSchema: tool.inputSchema,
        };
    }

    async callTool(name: string, input?: Record<string, unknown>): Promise<unknown> {
        const tool = this.tools.get(name);
        if (!tool) {
            const available = Array.from(this.tools.keys()).join(', ') || '(none)';
            throw new Error(`Unknown agent tool "${name}". Available tools: ${available}.`);
        }
        return tool.execute(input ?? {});
    }

    private publishToolToModelContext(tool: WebAgentToolDefinition): void {
        const modelContext = getModelContextApi();
        if (!modelContext) return;
        const payload = toModelContextPayload(tool);
        if (typeof modelContext.registerTool === 'function') {
            const controller = new AbortController();
            try {
                modelContext.registerTool(payload, { signal: controller.signal });
                this.modelContextAbortControllers.set(tool.name, controller);
            } catch {
                // A registration collision (e.g. duplicate name from another script)
                // must not break the window-registry channel.
            }
            return;
        }
        if (typeof modelContext.provideContext === 'function') {
            this.provideFullContext(modelContext);
        }
    }

    private provideFullContext(modelContext: ModelContextApi): void {
        if (typeof modelContext.provideContext !== 'function') return;
        // provideContext replaces the page's whole tool set. The bridge is expected
        // to be the only modelContext registrar in Trebble pages; if another
        // registrar appears, migrate it onto this bridge instead of calling
        // provideContext elsewhere.
        try {
            modelContext.provideContext({
                tools: Array.from(this.tools.values()).map(toModelContextPayload),
            });
        } catch {
            // Keep the window-registry channel working even if the standard API rejects.
        }
    }

    private refreshWindowRegistry(): void {
        const bridgeWindow = getBridgeWindow();
        if (!bridgeWindow) return;
        const registry: WebAgentWindowRegistry = {
            version: WINDOW_REGISTRY_VERSION,
            help: () => HELP_TEXT,
            list: () => this.listTools(),
            describe: (name: string) => this.describeTool(name),
            call: (name: string, input?: Record<string, unknown>) => this.callTool(name, input),
        };
        bridgeWindow[WINDOW_REGISTRY_KEY] = registry;
    }

    private queueAnnouncement(): void {
        if (this.announceQueued) return;
        this.announceQueued = true;
        const flush = () => {
            this.announceQueued = false;
            this.announce();
        };
        if (typeof queueMicrotask === 'function') {
            queueMicrotask(flush);
        } else {
            void Promise.resolve().then(flush);
        }
    }

    private announce(): void {
        const bridgeWindow = getBridgeWindow();
        if (!bridgeWindow) return;
        const names = Array.from(this.tools.keys());
        const namesKey = names.join(',');
        if (namesKey === this.lastAnnouncedNames) return;
        this.lastAnnouncedNames = namesKey;
        if (names.length === 0) return;
        // Deliberately console.info: browser-resident agents that read console output
        // discover the typed tool surface here instead of driving the UI blind.
        console.info(
            `[Trebble] ${names.length} in-page agent tool${names.length === 1 ? '' : 's'} available. `
            + `Discover: window.${WINDOW_REGISTRY_KEY}.list() · `
            + `schema: window.${WINDOW_REGISTRY_KEY}.describe(name) · `
            + `execute: await window.${WINDOW_REGISTRY_KEY}.call(name, input)`
        );
    }

    dispose(): void {
        this.unregisterTools(Array.from(this.tools.keys()));
    }
}

let sharedBridge: WebAgentToolBridge | null = null;

export function getWebAgentToolBridge(): WebAgentToolBridge {
    if (!sharedBridge) {
        sharedBridge = new WebAgentToolBridge();
    }
    return sharedBridge;
}

export function resetWebAgentToolBridgeForTests(): void {
    if (sharedBridge) sharedBridge.dispose();
    sharedBridge = null;
    const bridgeWindow = getBridgeWindow();
    if (bridgeWindow && WINDOW_REGISTRY_KEY in bridgeWindow) {
        delete bridgeWindow[WINDOW_REGISTRY_KEY];
    }
}

export const WEB_AGENT_TOOLS_WINDOW_KEY = WINDOW_REGISTRY_KEY;
